/**
 * Web Push helpers for the driver PWA.
 *
 * Usage from a React component:
 *   const status = await getPushStatus();
 *   if (status === "default") await subscribePush(api);
 */
import { api } from "@/lib/api";

const b64ToUint8 = (base64) => {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

export const isPushSupported = () =>
  "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

/** Returns "granted" | "denied" | "default" | "unsupported" */
export async function getPushStatus() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission;
}

async function getRegistration() {
  const reg = await navigator.serviceWorker.getRegistration();
  return reg || (await navigator.serviceWorker.register("/sw.js"));
}

/** Request permission (if needed) and subscribe. Returns the subscription. */
export async function subscribePush() {
  if (!isPushSupported()) throw new Error("Push notifications não suportadas neste browser");
  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") throw new Error("Permissão negada");

  const { data: keyData } = await api.get("/push/public-key");
  const reg = await getRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: b64ToUint8(keyData.public_key),
    });
  }
  await api.post("/driver/push/subscribe", { subscription: sub.toJSON() });
  return sub;
}

export async function unsubscribePush() {
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  try {
    await api.post("/driver/push/unsubscribe", { endpoint });
  } catch (e) {
    /* ignore */
  }
}

/** Trigger a self-test push (backend sends via our own subscription). */
export async function sendTestPush() {
  return api.post("/driver/push/test");
}
